import { StandardEvents } from '@shopify/events';

/** One product-gallery player for both Shopify media and retained theme assets. */
class MakerIsProductGallery extends HTMLElement {
  #controller;
  #variantRequest = 0;
  #zoomTrigger;

  connectedCallback() {
    if (this.#controller && !this.#controller.signal.aborted) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('play', this.#onPlay, { capture: true, signal });
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.querySelector('.mi-gallery-zoom')?.addEventListener('close', () => this.#zoomTrigger?.focus(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#variantRequest++;
    for (const video of this.querySelectorAll('video')) video.pause();
    for (const panel of this.querySelectorAll('[data-media-panel]')) this.#stopExternalVideo(panel);
    this.querySelector('.mi-gallery-zoom')?.close();
  }

  #onClick = (event) => {
    const button = event.target.closest('button');
    if (!button || button.closest('maker-is-product-gallery') !== this) return;
    if (button.hasAttribute('data-media-index')) this.selectMedia(button.dataset.mediaIndex);
    if (button.hasAttribute('data-close-zoom')) this.querySelector('.mi-gallery-zoom')?.close();
    if (button.hasAttribute('data-zoom-image')) {
      const dialog = this.querySelector('.mi-gallery-zoom');
      const image = button.querySelector('img');
      if (!dialog || !image) return;
      const enlarged = dialog.querySelector('img');
      enlarged.src = image.currentSrc || image.src;
      enlarged.alt = image.alt;
      this.#zoomTrigger = button;
      dialog.showModal();
    }
  };

  #onKeydown = (event) => {
    const current = event.target.closest('[data-media-index]');
    if (!current || current.closest('maker-is-product-gallery') !== this) return;
    const buttons = [...this.querySelectorAll('[data-media-index]')];
    const index = buttons.indexOf(current);
    let next;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % buttons.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = buttons.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    buttons[next].focus();
    this.selectMedia(buttons[next].dataset.mediaIndex);
  };

  #onPlay = (event) => {
    const panel = event.target.closest('[data-media-panel]');
    if (panel?.hidden) event.target.pause();
  };

  selectMedia(index) {
    const panels = [...this.querySelectorAll('[data-media-panel]')];
    const selected = panels.find(panel => panel.dataset.mediaPanel === index);
    if (!selected) return;
    for (const button of this.querySelectorAll('[data-media-index]')) {
      const active = button.dataset.mediaIndex === index;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    }
    for (const panel of panels) {
      panel.hidden = panel !== selected;
      if (panel.hidden) {
        for (const video of panel.querySelectorAll('video')) video.pause();
        this.#stopExternalVideo(panel);
      }
    }
    for (const video of selected.querySelectorAll('video')) {
      if (video.ended) video.currentTime = 0;
      // Invoke synchronously in the thumbnail gesture, without timers or deferred
      // loading. Keep native controls if a browser blocks playback or a file fails.
      const playback = video.play();
      playback?.then(() => {
        if (!this.isConnected || selected.hidden) video.pause();
      }).catch(() => {}); // Switching away can legitimately abort an in-flight play.
    }
    const external = selected.querySelector('template[data-external-video]');
    if (external && !selected.querySelector('iframe')) {
      const content = external.content.cloneNode(true);
      const frame = content.querySelector('iframe');
      if (frame) {
        const url = new URL(frame.src);
        url.searchParams.set('autoplay', '1');
        frame.src = url.href;
        frame.allow = 'autoplay; fullscreen; picture-in-picture';
      }
      selected.appendChild(content);
    }
  }

  #stopExternalVideo(panel) {
    // Unload instead of sending messages to an arbitrary third-party origin.
    if (panel.querySelector('template[data-external-video]')) panel.querySelector('iframe')?.remove();
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const request = ++this.#variantRequest;
    event.promise?.then(({ detail }) => {
      if (!this.isConnected || request !== this.#variantRequest || !detail?.html) return;
      const next = detail.html.querySelector('maker-is-product-gallery');
      if (!next) return;
      // Shopify places the selected variant's image first. Preserve the player
      // only when both the media and their order are unchanged; sorting these
      // keys would hide a variant-image change within the same media set.
      const identity = gallery => [...gallery.querySelectorAll('[data-media-panel]')]
        .map(panel => panel.dataset.mediaPanel).join('|');
      if (next.dataset.productId === this.dataset.productId && identity(next) === identity(this)) return;
      this.replaceWith(next);
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[product-gallery] Unable to update product media.');
    });
  };
}

if (!customElements.get('maker-is-product-gallery')) {
  customElements.define('maker-is-product-gallery', MakerIsProductGallery);
}
